import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { backofficeFetch } from '@/services/backoffice-api';

export interface BackofficePreference<
  TValue extends Record<string, unknown> = Record<string, unknown>,
> {
  scope: string;
  schemaVersion: number;
  value: TValue;
  updatedAt: string;
}

interface BackofficePreferencesResponse {
  preferences: BackofficePreference[];
}

interface BackofficePreferenceResponse {
  preference: BackofficePreference;
}

interface DeleteBackofficePreferenceResponse {
  deleted: boolean;
}

function buildPreferenceQuery(scopes: string[]): string {
  const params = new URLSearchParams();
  for (const scope of scopes) {
    params.append('scope', scope);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

export async function fetchBackofficePreferences(
  scopes: string[],
  signal?: AbortSignal
): Promise<BackofficePreference[]> {
  const response = await backofficeFetch(`/preferences${buildPreferenceQuery(scopes)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) {
    throw new Error(`Failed to load preferences (${response.status})`);
  }

  const body = (await response.json()) as BackofficePreferencesResponse;
  return body.preferences;
}

export async function upsertBackofficePreference<TValue extends Record<string, unknown>>(
  scope: string,
  value: TValue,
  schemaVersion = 1,
  signal?: AbortSignal
): Promise<BackofficePreference<TValue>> {
  const response = await backofficeFetch(`/preferences/${encodeURIComponent(scope)}`, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ schemaVersion, value }),
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) {
    throw new Error(`Failed to save preference (${response.status})`);
  }

  const body = (await response.json()) as BackofficePreferenceResponse;
  return body.preference as BackofficePreference<TValue>;
}

const preferenceCache = new Map<string, BackofficePreference>();

export async function deleteBackofficePreference(
  scope: string,
  signal?: AbortSignal
): Promise<DeleteBackofficePreferenceResponse> {
  const response = await backofficeFetch(`/preferences/${encodeURIComponent(scope)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) {
    throw new Error(`Failed to delete preference (${response.status})`);
  }

  preferenceCache.delete(scope);
  return (await response.json()) as DeleteBackofficePreferenceResponse;
}

export async function resetAllBackofficePreferences(signal?: AbortSignal): Promise<number> {
  const preferences = await fetchBackofficePreferences([], signal);

  for (const preference of preferences) {
    await deleteBackofficePreference(preference.scope, signal);
  }

  preferenceCache.clear();
  return preferences.length;
}

export function useBackofficePreference<TValue extends Record<string, unknown>>(
  scope: string | undefined,
  options: {
    schemaVersion?: number;
    defaultValue: TValue;
    debounceMs?: number;
  }
) {
  const schemaVersion = options.schemaVersion ?? 1;
  const debounceMs = options.debounceMs ?? 600;
  const [value, setValueState] = useState<TValue>(options.defaultValue);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const valueRef = useRef(value);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveAbortRef = useRef<AbortController | null>(null);
  const hasHydratedRef = useRef(false);

  valueRef.current = value;

  useEffect(() => {
    if (!scope) {
      setValueState(options.defaultValue);
      setLoaded(true);
      hasHydratedRef.current = true;
      return;
    }

    const cached = preferenceCache.get(scope);
    if (cached) {
      setValueState({ ...options.defaultValue, ...(cached.value as TValue) });
      setLoaded(true);
      hasHydratedRef.current = true;
      return;
    }

    const controller = new AbortController();
    setLoaded(false);
    setLoadError(null);
    hasHydratedRef.current = false;

    const loadTimer = setTimeout(() => {
      fetchBackofficePreferences([scope], controller.signal)
        .then((preferences) => {
          const preference = preferences.find((item) => item.scope === scope);
          if (preference) {
            preferenceCache.set(scope, preference);
          }
          setValueState({
            ...options.defaultValue,
            ...((preference?.value ?? {}) as TValue),
          });
        })
        .catch((caught: unknown) => {
          if (caught instanceof DOMException && caught.name === 'AbortError') {
            return;
          }
          setValueState(options.defaultValue);
          setLoadError(caught instanceof Error ? caught : new Error('Failed to load preference'));
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setLoaded(true);
            hasHydratedRef.current = true;
          }
        });
    }, 0);

    return () => {
      clearTimeout(loadTimer);
      controller.abort();
    };
  }, [options.defaultValue, scope]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      saveAbortRef.current?.abort();
    };
  }, []);

  const setValue = useCallback(
    (nextValue: TValue | ((current: TValue) => TValue)) => {
      setValueState((current) => {
        const resolved =
          typeof nextValue === 'function'
            ? (nextValue as (current: TValue) => TValue)(current)
            : nextValue;

        if (!scope || !hasHydratedRef.current) {
          return resolved;
        }

        if (saveTimerRef.current) {
          clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = setTimeout(() => {
          saveAbortRef.current?.abort();
          const controller = new AbortController();
          saveAbortRef.current = controller;
          void upsertBackofficePreference(scope, resolved, schemaVersion, controller.signal)
            .then((preference) => {
              preferenceCache.set(scope, preference);
            })
            .catch(() => undefined);
        }, debounceMs);

        return resolved;
      });
    },
    [debounceMs, schemaVersion, scope]
  );

  const resetValue = useCallback(() => {
    setValue(options.defaultValue);
  }, [options.defaultValue, setValue]);

  return useMemo(
    () => ({
      value,
      setValue,
      resetValue,
      loaded,
      loadError,
    }),
    [loadError, loaded, resetValue, setValue, value]
  );
}
